import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { mondayGraphQL } from '../lib/monday'
import { pingGHL } from '../lib/ghl'

const POLL_INTERVAL = 60_000 // 60 seconds
const TIMEOUT_MS = 5_000     // 5 second timeout per check

// Service definitions
const SERVICES = [
  { key: 'supabase', label: 'Supabase', color: '#3ECF8E' },
  { key: 'monday',   label: 'Monday',   color: '#6161FF' },
  { key: 'ghl',      label: 'GHL',      color: '#FF6B35' },
]

// Status dot colors
const STATUS_COLORS = {
  ok:       'bg-emerald-400',
  down:     'bg-red-500',
  checking: 'bg-amber-400 animate-pulse',
  idle:     'bg-gray-300',
}

/** Race a promise against a timeout */
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms)),
  ])
}

export default function ServiceHealthBar() {
  const { t } = useTranslation()
  const [statuses, setStatuses] = useState({
    supabase: { status: 'idle', ms: null, lastCheck: null },
    monday:   { status: 'idle', ms: null, lastCheck: null },
    ghl:      { status: 'idle', ms: null, lastCheck: null },
  })
  const intervalRef = useRef(null)
  const mountedRef = useRef(true)

  const checkService = useCallback(async (key) => {
    if (!mountedRef.current) return

    setStatuses(prev => ({
      ...prev,
      [key]: { ...prev[key], status: 'checking' },
    }))

    const start = performance.now()
    let status = 'down'
    let ms = null

    try {
      switch (key) {
        case 'supabase': {
          const { error } = await withTimeout(
            supabase.from('posts').select('id').limit(1),
            TIMEOUT_MS
          )
          if (!error) status = 'ok'
          break
        }
        case 'monday': {
          await withTimeout(
            mondayGraphQL('{ me { name } }'),
            TIMEOUT_MS
          )
          status = 'ok'
          break
        }
        case 'ghl': {
          const res = await withTimeout(pingGHL('construction'), TIMEOUT_MS)
          if (res?.status === 'ok') status = 'ok'
          break
        }
      }
    } catch {
      status = 'down'
    }

    ms = Math.round(performance.now() - start)

    if (mountedRef.current) {
      setStatuses(prev => ({
        ...prev,
        [key]: { status, ms, lastCheck: new Date() },
      }))
    }
  }, [])

  const checkAll = useCallback(() => {
    SERVICES.forEach(s => checkService(s.key))
  }, [checkService])

  // Check on mount + set up polling
  useEffect(() => {
    mountedRef.current = true
    checkAll()

    intervalRef.current = setInterval(checkAll, POLL_INTERVAL)

    return () => {
      mountedRef.current = false
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [checkAll])

  const formatTime = (date) => {
    if (!date) return ''
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  return (
    <div className="flex flex-wrap gap-1.5 sm:gap-2 items-center">
      {SERVICES.map(({ key, label }) => {
        const s = statuses[key]
        const dotClass = STATUS_COLORS[s.status] || STATUS_COLORS.idle

        // Build tooltip text
        let tooltip = label
        if (s.status === 'checking') {
          tooltip += ` — ${t('admin.health.checking', 'Checking...')}`
        } else if (s.status === 'ok') {
          tooltip += ` — ${t('admin.health.ok', 'Online')}`
          if (s.ms != null) tooltip += ` (${s.ms}ms)`
        } else if (s.status === 'down') {
          tooltip += ` — ${t('admin.health.down', 'Offline')}`
        }
        if (s.lastCheck) {
          tooltip += `\n${t('admin.health.last_check', 'Last check')}: ${formatTime(s.lastCheck)}`
        }

        return (
          <button
            key={key}
            type="button"
            onClick={checkAll}
            title={tooltip}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-gray-500 bg-gray-50 hover:bg-gray-100 rounded-full border border-gray-200/80 transition-colors cursor-pointer select-none"
          >
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} />
            <span className="hidden xs:inline sm:inline">{label}</span>
          </button>
        )
      })}
    </div>
  )
}
